interface SetIndicatored {
  indicator: 'warmup' | 'dropset' | 'failure' | 'normal' | number;
}

interface Indexed {
  index: number;
}

/**
 * Accepts any data structure like this:
 * [
 *  {indicator: 'warmup', index: 0, ...},
 *  {indicator: 'normal', index: 1, ...},
 *  {indicator: 'warmup', index: 2, ...},
 *  {indicator: 'normal', index: 3, ...},
 *  {indicator: 'normal', index: 4, ...},
 *  {indicator: 'failure', index: 5, ...},
 *  {indicator: 'dropset', index: 6, ...},
 * ]
 *
 *
 * And outputs:
 * ```
 * [
 *  {indicator: 'warmup', index: 0, ...},
 *  {indicator: 'normal', index: 0, ...},
 *  {indicator: 'warmup', index: 1, ...},
 *  {indicator: 'normal', index: 2, ...},
 *  {indicator: 'normal', index: 3, ...},
 *  {indicator: 'failure', index: 4, ...},
 *  {indicator: 'dropset', index: 0, ...},
 * ]
 * ```
 */

export const indexSetObjectsForUI = <T>(
  indexedSet: SetIndicatored[],
): Array<SetIndicatored & Indexed & T> => {
  return indexedSet.reduce(
    (accu: any, currentSet: SetIndicatored) => {
      if (currentSet.indicator === 'warmup') {
        const newSet = { ...currentSet, index: accu.warmupCount };
        accu.warmupCount += 1;
        accu.sets = [...accu.sets, newSet];
      } else if (currentSet.indicator === 'dropset') {
        const newSet = { ...currentSet, index: accu.dropsetCount };
        accu.dropsetCount += 1;
        accu.sets = [...accu.sets, newSet];
      } else {
        const newSet = { ...currentSet, index: accu.normalCount };
        accu.normalCount += 1;
        accu.sets = [...accu.sets, newSet];
      }

      return accu;
    },
    { warmupCount: 0, dropsetCount: 0, normalCount: 0, sets: [] },
  ).sets;
};

/**
 * This function is almost the exact same as `indexSetObjectsForUI`
 * but this function will change failure sets to normal. Because when
 * processing failure sets, they are often considered normal sets.
 * For example when calculating a previous values, failure sets are
 * considered normal sets.
 *
 * Accepts any data structure like this:
 * [
 *  {indicator: 'warmup', index: 0, ...},
 *  {indicator: 'normal', index: 1, ...},
 *  {indicator: 'warmup', index: 2, ...},
 *  {indicator: 'normal', index: 3, ...},
 *  {indicator: 'normal', index: 4, ...},
 *  {indicator: 'failure', index: 5, ...},
 *  {indicator: 'dropset', index: 6, ...},
 * ]
 *
 *
 * And outputs:
 * ```
 * [
 *  {indicator: 'warmup', index: 0, ...},
 *  {indicator: 'normal', index: 0, ...},
 *  {indicator: 'warmup', index: 1, ...},
 *  {indicator: 'normal', index: 2, ...},
 *  {indicator: 'normal', index: 3, ...},
 *  {indicator: 'normal', index: 4, ...}, // Note the failure set was flipped to normal.
 *  {indicator: 'dropset', index: 0, ...},
 * ]
 * ```
 */

export const indexSetObjectsForType = <T>(
  indexedSet: SetIndicatored[],
): Array<SetIndicatored & Indexed & T> => {
  return indexedSet.reduce(
    (accu: any, currentSet: SetIndicatored) => {
      if (currentSet.indicator === 'warmup') {
        const newSet = { ...currentSet, index: accu.warmupCount };
        accu.warmupCount += 1;
        accu.sets = [...accu.sets, newSet];
      } else if (currentSet.indicator === 'dropset') {
        const newSet = { ...currentSet, index: accu.dropsetCount };
        accu.dropsetCount += 1;
        accu.sets = [...accu.sets, newSet];
      } else {
        const newSet = {
          ...currentSet,
          indicator: 'normal',
          index: accu.normalCount,
        };
        accu.normalCount += 1;
        accu.sets = [...accu.sets, newSet];
      }

      return accu;
    },
    { warmupCount: 0, dropsetCount: 0, normalCount: 0, sets: [] },
  ).sets;
};

export type UserFacingSetIndicator = 'dropset' | 'warmup' | 'failure' | number;

export const setObjectToUIFacingSetIndicator = (
  set: SetIndicatored & Indexed,
): UserFacingSetIndicator => {
  if (set.indicator === 'warmup' || set.indicator === 'dropset' || set.indicator === 'failure') {
    return set.indicator;
  }

  return set.index + 1;
};

export const compareUserFacingSetIndicator = (
  indicatorA: UserFacingSetIndicator,
  indicatorB: UserFacingSetIndicator,
) => {
  const normalizedA =
    indicatorA === 'warmup' ? 'warmup' : indicatorA === 'dropset' ? 'dropset' : 'normal';
  const normalizedB =
    indicatorB === 'warmup' ? 'warmup' : indicatorB === 'dropset' ? 'dropset' : 'normal';

  return normalizedA === normalizedB;
};
